<template>
  <div class="survey-container">
    <Toast ref="toast" />
    <ProgressSpinner v-if="loading" />
    <div class="survey-step">
      <!-- Прогресс-бар -->
      <ProgressBar :progress="currentProgress" :accentColor="accentColor" />

      <!-- Вопрос -->
      <h2>{{ currentQuestion.text }}</h2>

      <!-- Тип 1: Выбор одного варианта ответа -->
      <div v-if="currentQuestion.type === 'single-choice'" class="options">
        <div
          v-for="option in currentQuestion.options"
          :key="option.value"
          class="option"
          :class="{
            selected: selectedOptions[currentQuestion.name] === option.value,
          }"
          @click="selectOption(currentQuestion.name, option.value)"
        >
          <span>{{ option.label }}</span>
        </div>
      </div>

      <!-- Тип 2: Поля для ввода текста -->

      <div v-if="currentQuestion.type === 'input-fields'" class="input-fields">
        <FloatLabel
          variant="on"
          v-for="(field, index) in currentQuestion.fields"
          :key="index"
        >
          <InputText
            :id="field.name"
            class="w-100"
            size="large"
            v-model="inputs[field.name]"
          />
          <label :for="field.name">{{ field.label }}</label>
        </FloatLabel>
      </div>

      <!-- Тип 3: Выбор нескольких вариантов ответа -->
      <div v-if="currentQuestion.type === 'multiple-choice'" class="options">
        <div
          v-for="option in currentQuestion.options"
          :key="option.value"
          class="option"
          :class="{
            selected:
              selectedOptions[currentQuestion.name] &&
              selectedOptions[currentQuestion.name].includes(option.value),
          }"
          @click="toggleOption(currentQuestion.name, option.value)"
        >
          <span>{{ option.label }}</span>
        </div>
      </div>

      <!-- Навигация -->
      <div class="navigation">
        <button @click="goBack" :disabled="!canGoBack">Назад</button>
        <button @click="goNext" :disabled="!canGoNext">Далее</button>
      </div>
    </div>
  </div>
</template>

<script>
import ProgressBar from "./ProgressBar.vue";
import {
  sendSurveyData,
  addWeight,
  fetchUserProfile,
  fetchLatestLipidsData,
} from "../services/api";
import Toast from "primevue/toast";
import ProgressSpinner from "primevue/progressspinner";
import { ref } from "vue";
import { calculateRisks, calculateRisksAsync } from "../utils/riskCalculator";
import { loadFromLocalDB } from "../services/dataService";

export default {
  name: "SurveyStep",
  components: { ProgressBar, Toast, ProgressSpinner },
  data() {
    return {
      // Вопросы и их варианты
      questions: [
        {
          name: "params",
          type: "input-fields",
          text: "Введите ваши показатели:",
          fields: [
            {
              name: "TyHeight",
              label: "Рост (см):",
              type: "number",
              placeholder: "Введите ваш рост",
              required: true,
            },
            {
              name: "TyWeight",
              label: "Масса тела (кг):",
              type: "number",
              placeholder: "Введите вашу массу тела",
              required: true,
            },
            {
              name: "SystolicPressure",
              label: "Систолическое давление (верхнее):",
              type: "number",
              placeholder: "Введите ваше давление",
              required: true,
            },
            {
              name: "DiastolicPressure",
              label: "Диастолическое давление (нижнее):",
              type: "number",
              placeholder: "Введите ваше давление",
              required: true,
            },
          ],
        },
        {
          name: "smoking",
          type: "single-choice",
          text: "Вы курите сигареты или вейпы?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "diabetes",
          type: "single-choice",
          text: "У вас есть сахарный диабет или отмечалось повышение глюкозы крови?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        //// Вопросы про себя
        {
          name: "question_1",
          type: "single-choice",
          text: "Переносили ли вы инфаркт миокарда или инсульт в молодом возрасте (у мужчин до 55 лет, у женщин до 60 лет)?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_2",
          type: "single-choice",
          text: "Выявляли ли у вас атеросклеротичесике бляшки?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_3",
          type: "single-choice",
          text: "Отмечали ли вы у себя наличие ксантом или ксантелазм?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_4",
          type: "single-choice",
          text: "Отмечали ли вы у себя наличе сероватого круга по краю роговицы (липидной дуги)?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_5",
          type: "single-choice",
          text: "Были ли у ваших ближайших родственников инфаркты или инсульты в молодом возрасте (у мужчин до 55 лет, у женщин до 60 лет)?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_6",
          type: "single-choice",
          text: "Регистрировалось ли у ваших ближайших родственников повышение ЛПНП более 5 ммоль/л?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_7",
          type: "single-choice",
          text: "Отмечались ли у ваших ближайших родственников ксантомы и/или ксантелазмы?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },

        {
          name: "question_8",
          type: "single-choice",
          text: "Регистрировалось ли у ваших детей повышение ЛПНП более 4 ммоль/л?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
        {
          name: "question_9",
          type: "single-choice",
          text: "Проводилась ли ранее генодиагностика наследственных нарушений липидного обмена (гены LDLR, APOB или PCSK9) ?",
          options: [
            { label: "Да", value: "yes" },
            { label: "Нет", value: "no" },
          ],
        },
      ],
      currentStep: 0, // Индекс текущего шага
      selectedOptions: {}, // Объект для хранения выбранных ответов
      inputs: {
        TyHeight: "",
        TyWeight: "",
        SystolicPressure: "",
        DiastolicPressure: "",
        smoking: "",
        diabetes: "",
        question_1: "",
        question_2: "",
        question_3: "",
        question_4: "",
        question_5: "",
        question_6: "",
        question_7: "",
        question_8: "",
        question_9: "",
      },
      accentColor: "#4caf50", // Акцентный цвет
      loading: false, // состояние загрузки
    };
  },
  computed: {
    // Текущий вопрос
    currentQuestion() {
      return this.questions[this.currentStep];
    },
    // Прогресс выполнения
    currentProgress() {
      return ((this.currentStep + 1) / this.questions.length) * 100;
    },
    // Возможность вернуться назад
    canGoBack() {
      return true; // Кнопка "Назад" всегда активна
    },
    // Проверка, можно ли перейти дальше
    // Проверка, можно ли перейти дальше
    canGoNext() {
      if (this.currentQuestion.type === "single-choice") {
        return !!this.selectedOptions[this.currentQuestion.name];
      } else if (this.currentQuestion.type === "input-fields") {
        // Проверяем, заполнены ли все обязательные поля
        return this.currentQuestion.fields.every(
          (field) => !field.required || this.inputs[field.name]
        );
      } else if (this.currentQuestion.type === "multiple-choice") {
        return (
          this.selectedOptions[this.currentQuestion.name] &&
          this.selectedOptions[this.currentQuestion.name].length > 0
        );
      }
      return false;
    },
  },
  methods: {
    initializeInputs() {
      // Инициализация значений полей при загрузке компонента или смене вопроса
      if (this.currentQuestion.type === "input-fields") {
        this.currentQuestion.fields.forEach((field) => {
          if (!this.inputs[field.name]) {
            this.$set(this.inputs, field.name, "");
          }
        });
      }
    },
    // Для single-choice
    selectOption(questionName, value) {
      this.selectedOptions[questionName] = value;
      console.log(this.selectedOptions); // Для отладки
    },
    // Для multiple-choice
    toggleOption(questionName, value) {
      if (
        this.selectedOptions[questionName] &&
        this.selectedOptions[questionName].includes(value)
      ) {
        this.selectedOptions[questionName] = this.selectedOptions[
          questionName
        ].filter((v) => v !== value);
      } else {
        if (!this.selectedOptions[questionName]) {
          this.$set(this.selectedOptions, questionName, []);
        }
        this.selectedOptions[questionName].push(value);
      }
    },
    // Переход на предыдущий шаг
    goBack() {
      if (this.currentStep === 0) {
        this.$router.push("/home"); // Возврат на главную страницу при нажатии "Назад" на первом шаге
      } else {
        this.currentStep--;
        this.resetSelection2();
        this.initializeInputs();
      }
    },
    // Переход на следующий шаг
    goNext() {
      if (this.canGoNext) {
        if (this.currentStep === this.questions.length - 1) {
          this.submitSurvey();
        } else {
          this.currentStep++;
          this.resetSelection2();
          this.initializeInputs();
        }
      }
    },
    // Сброс выбора при смене шага
    resetSelection() {
      this.selectedOptions = {};
      this.inputs.TyHeight = "";
      this.inputs.TyWeight = "";
    },
    resetSelection2() {
      //this.selectedOptions = {};
    },
    async getUserDataFromAPI() {
      try {
        console.log("Получение данных пользователя из API...");

        // Сначала пробуем загрузить из локальной БД
        let profileData = await loadFromLocalDB("profile");

        // Если данных нет в локальной БД, загружаем с сервера
        if (!profileData) {
          profileData = await fetchUserProfile();
        }

        if (profileData) {
          // Преобразуем значение пола для соответствия формату
          const gender = profileData.sex === "Мужской" ? "male" : "female";

          // Рассчитываем возраст из даты рождения
          let age = null;
          if (profileData.birth_date) {
            const birthDate = new Date(profileData.birth_date);
            const today = new Date();
            age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            if (
              monthDiff < 0 ||
              (monthDiff === 0 && today.getDate() < birthDate.getDate())
            ) {
              age--;
            }
          }

          // Сохраняем данные в localStorage
          const userData = {
            gender: gender,
            birth_date: profileData.birth_date,
            age: age,
          };
          localStorage.setItem("user_data", JSON.stringify(userData));
          console.log(
            "Данные пользователя получены из API и сохранены в localStorage:",
            userData
          );

          return userData;
        }
      } catch (error) {
        console.error(
          "Ошибка при получении данных пользователя из API:",
          error
        );
      }
      return null;
    },

    // Функция для получения данных липидного профиля из API
    async getLipidDataFromAPI() {
      try {
        console.log("Получение данных липидного профиля из API...");

        // Получаем последние данные липидного профиля
        const lipidData = await fetchLatestLipidsData();

        if (lipidData) {
          // Создаем объект с данными для localStorage
          const lipidDataForStorage = {
            total_cholesterol: lipidData.total_cholesterol,
            hdl: lipidData.hdl,
          };

          // Сохраняем данные в localStorage
          localStorage.setItem(
            "lipid_data",
            JSON.stringify(lipidDataForStorage)
          );
          console.log(
            "Данные липидного профиля получены из API и сохранены в localStorage:",
            lipidDataForStorage
          );

          return lipidDataForStorage;
        }
      } catch (error) {
        console.error(
          "Ошибка при получении данных липидного профиля из API:",
          error
        );
      }
      return null;
    },

    async submitSurvey() {
      this.loading = true;
      try {
        const weight = parseInt(this.inputs.TyWeight, 10) || 0;
        const height = parseInt(this.inputs.TyHeight, 10) || 0;
        const bmi = height > 0 ? +(weight / (height / 100) ** 2).toFixed(1) : 0;

        // Получаем данные пользователя из localStorage
        let gender = null;
        let age = null;
        let cholesterol = null;
        let hdl = null;

        try {
          // Получаем пол и возраст из localStorage
          const userDataString = localStorage.getItem("user_data");
          let userData = null;

          if (userDataString) {
            userData = JSON.parse(userDataString);
            gender = userData.gender;

            // Если есть возраст, используем его
            if (userData.age) {
              age = userData.age;
            }
            // Если есть дата рождения, вычисляем возраст
            else if (userData.birth_date) {
              const birthDate = new Date(userData.birth_date);
              const today = new Date();
              let calculatedAge = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();

              if (
                monthDiff < 0 ||
                (monthDiff === 0 && today.getDate() < birthDate.getDate())
              ) {
                calculatedAge--;
              }

              age = calculatedAge;
            }
          } else {
            // Если данных нет в localStorage, получаем их из API
            console.log(
              "Данные пользователя отсутствуют в localStorage, получаем из API"
            );
            userData = await this.getUserDataFromAPI();

            if (userData) {
              gender = userData.gender;
              age = userData.age;
            } else {
              console.warn("Не удалось получить данные пользователя из API");
            }
          }

          // Получаем данные о холестерине из localStorage
          const lipidDataString = localStorage.getItem("lipid_data");
          let lipidData = null;

          if (lipidDataString) {
            lipidData = JSON.parse(lipidDataString);
            cholesterol = lipidData.total_cholesterol;
            hdl = lipidData.hdl;
          } else {
            // Если данных о липидном профиле нет в localStorage, получаем их из API
            console.log(
              "Данные липидного профиля отсутствуют в localStorage, получаем из API"
            );
            lipidData = await this.getLipidDataFromAPI();

            if (lipidData) {
              cholesterol = lipidData.total_cholesterol;
              hdl = lipidData.hdl;
            } else {
              console.warn(
                "Не удалось получить данные липидного профиля из API"
              );
            }
          }

          console.log("Получены данные пользователя:", {
            gender,
            age,
            cholesterol,
            hdl,
          });
        } catch (e) {
          console.error(
            "Ошибка при получении данных пользователя из localStorage:",
            e
          );
        }

        // Создаем объект с данными для расчета рисков
        const surveyData = {
          height: height,
          weight: weight,
          smoke: this.selectedOptions["smoking"] === "yes",
          sb_pressure: parseInt(this.inputs.SystolicPressure, 10) || 0,
          db_pressure: parseInt(this.inputs.DiastolicPressure, 10) || 0,
          question_0: this.selectedOptions["diabetes"] === "yes",
          question_1: this.selectedOptions["question_1"] === "yes",
          question_2: this.selectedOptions["question_2"] === "yes",
          question_3: this.selectedOptions["question_3"] === "yes",
          question_4: this.selectedOptions["question_4"] === "yes",
          question_5: this.selectedOptions["question_5"] === "yes",
          question_6: this.selectedOptions["question_6"] === "yes",
          question_7: this.selectedOptions["question_7"] === "yes",
          question_8: this.selectedOptions["question_8"] === "yes",
          question_9: this.selectedOptions["question_9"] === "yes",
          gender: gender,
          age: age,
          cholesterol: cholesterol,
          hdl: hdl,
        };

        console.log("Данные для расчета риска:", surveyData);

        // Получаем результаты расчета рисков, используя асинхронную функцию
        const risks = await calculateRisksAsync(surveyData);
        console.log("Результаты расчета рисков:", risks);

        // Объединяем данные опроса с результатами расчета рисков
        const finalData = {
          ...surveyData,
          ...risks,
        };

        console.log("Отправляемые данные:", finalData);
        const response = await sendSurveyData(finalData);
        console.log("Ответ сервера:", response);

        // Проверяем успешность запроса
        if (response) {
          // Добавляем запись веса
          if (weight > 0 && height > 0) {
            try {
              await addWeight({
                current_weight: weight,
                bmi: bmi,
              });
              console.log("Вес успешно добавлен в базу весов");
            } catch (weightError) {
              console.error("Ошибка при добавлении веса:", weightError);
              // Не прерываем выполнение, так как основной опрос уже сохранен
            }
          }

          this.$refs.toast.add({
            severity: "success",
            summary: "Успех",
            detail: "Опрос успешно отправлен",
            life: 3000,
          });
          this.$router.push("/home");
        } else {
          throw new Error("Ошибка при отправке данных: нет ответа от сервера");
        }
      } catch (error) {
        console.error("Ошибка:", error);
        this.$refs.toast.add({
          severity: "error",
          summary: "Ошибка",
          detail:
            error.message || "Ошибка при отправке опроса. Попробуйте снова.",
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    getAnswer(questionName) {
      return this.selectedOptions[questionName] === "yes";
    },
  },
};
</script>

<style scoped>
.survey-step {
  max-width: 400px;
  min-width: 100vw;
  margin: 40px auto;
  margin-top: 0px;
  margin-bottom: 0px;
  padding: 20px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: space-between;

  padding-bottom: 130px;
  padding-top: 50px;
}

h2 {
  font-size: 18px;
  text-align: center;
  margin-bottom: 20px;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.option.selected {
  background-color: var(--accent-color, #4caf50);
  color: #fff;
  border-color: var(--accent-color, #4caf50);
}
.input-fields {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

label {
  display: flex;
  flex-direction: column;
  font-size: 14px;
}

input {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  min-width: 100%;
}
.navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

button {
  padding: 10px 20px;
  border: none;
  background: #4caf50;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s;
}

button:disabled {
  background: #ccc;
  cursor: not-allowed;
}
.survey-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
